import { z } from 'zod';
import type { FieldConfig } from '@/components/blocks/forms';
import type {
  AssertFieldKeysMatch,
  AssertSchemaKeyCoverage,
  SchemaFieldKeys,
} from '@/components/blocks/forms/schemaSync';
import { optionalDate, optionalEmail, optionalText, requiredText } from '@/lib/formSchemas';
import { personnelFieldTooltips } from './masterDataFieldTooltips';

export const personnelFormSchema = z
  .object({
    firstName: requiredText(1, 80, {
      min: 'Inserisci il nome',
      max: 'Massimo 80 caratteri',
    }),
    lastName: requiredText(1, 80, {
      min: 'Inserisci il cognome',
      max: 'Massimo 80 caratteri',
    }),
    email: optionalEmail().optional(),
    phone: optionalText(40),
    role: requiredText(2, 120, {
      min: 'Inserisci il ruolo',
      max: 'Massimo 120 caratteri',
    }),
    scope: z
      .enum(['company', 'site'], {
        message: 'Seleziona un valore',
      })
      .default('company'),
    siteId: z.preprocess((value) => (value === '' ? undefined : value), z.string().optional()),
    reportRecipient: z.boolean().default(false),
    alarmContact: z.boolean().default(false),
    fireTraining: z.boolean().default(false),
    firstAidTraining: z.boolean().default(false),
    licensed: z.boolean().default(false),
    licenseExpiry: optionalDate(),
    licenseLastUpdate: optionalDate(),
    reperibile: z.boolean().default(false),
  })
  .superRefine((values, ctx) => {
    if (values.scope === 'site' && !values.siteId) {
      ctx.addIssue({
        path: ['siteId'],
        code: z.ZodIssueCode.custom,
        message: 'Seleziona uno stabilimento',
      });
    }

    if (values.reportRecipient && !values.email) {
      ctx.addIssue({
        path: ['email'],
        code: z.ZodIssueCode.custom,
        message: "Inserisci l'email per l'invio verbali",
      });
    }

    if ((values.alarmContact || values.reperibile) && !values.phone) {
      ctx.addIssue({
        path: ['phone'],
        code: z.ZodIssueCode.custom,
        message: 'Inserisci il telefono per i contatti urgenti',
      });
    }

    if (values.licensed) {
      if (!values.licenseExpiry) {
        ctx.addIssue({
          path: ['licenseExpiry'],
          code: z.ZodIssueCode.custom,
          message: 'Inserisci la scadenza patente',
        });
      }
      if (!values.licenseLastUpdate) {
        ctx.addIssue({
          path: ['licenseLastUpdate'],
          code: z.ZodIssueCode.custom,
          message: "Inserisci l'ultimo aggiornamento patente",
        });
      }
    }
  });

export const personnelFormFieldKeys = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'role',
  'scope',
  'siteId',
  'reportRecipient',
  'alarmContact',
  'fireTraining',
  'firstAidTraining',
  'licensed',
  'licenseLastUpdate',
  'licenseExpiry',
  'reperibile',
] as const satisfies SchemaFieldKeys<typeof personnelFormSchema>;

const _personnelFormSchemaCoverageCheck: AssertSchemaKeyCoverage<
  typeof personnelFormSchema,
  typeof personnelFormFieldKeys
> = true;
void _personnelFormSchemaCoverageCheck;

export const personnelFormFields = [
  {
    key: 'firstName',
    label: 'Nome',
    type: 'text',
    required: true,
    placeholder: 'Es: Mario',
  },
  {
    key: 'lastName',
    label: 'Cognome',
    type: 'text',
    required: true,
    placeholder: 'Es: Rossi',
  },
  {
    key: 'role',
    label: 'Ruolo',
    type: 'text',
    required: true,
    placeholder: 'Es: Responsabile Sicurezza',
  },
  {
    key: 'scope',
    label: 'Ambito',
    type: 'select',
    placeholder: 'Seleziona ambito',
    tooltip: personnelFieldTooltips.scope,
    options: [
      { value: 'company', label: 'Azienda intera' },
      { value: 'site', label: 'Stabilimento specifico' },
    ],
  },
  {
    key: 'siteId',
    label: 'Stabilimento',
    type: 'select',
    placeholder: 'Seleziona stabilimento',
    tooltip: personnelFieldTooltips.siteId,
    disabled: (values) => values.scope !== 'site',
    description: "Compila solo se l'ambito è stabilimento.",
    dependsOn: ['scope'],
  },
  {
    key: 'reportRecipient',
    label: 'Recapito invio verbali',
    type: 'checkbox',
    tooltip: personnelFieldTooltips.reportRecipient,
  },
  {
    key: 'alarmContact',
    label: 'Riferimento in caso di allarme',
    type: 'checkbox',
    tooltip: personnelFieldTooltips.alarmContact,
  },
  {
    key: 'reperibile',
    label: 'Reperibile',
    type: 'checkbox',
    tooltip: personnelFieldTooltips.reperibile,
  },
  {
    key: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'Es: mario.rossi@azienda.it',
    tooltip: personnelFieldTooltips.email,
    colSpan: 2,
  },
  {
    key: 'phone',
    label: 'Telefono',
    type: 'tel',
    placeholder: 'Es: +39 333 1234567',
    tooltip: personnelFieldTooltips.phone,
    colSpan: 2,
  },
  {
    key: 'fireTraining',
    label: 'Corso antincendio',
    type: 'checkbox',
    tooltip: personnelFieldTooltips.fireTraining,
  },
  {
    key: 'firstAidTraining',
    label: 'Corso primo soccorso',
    type: 'checkbox',
    tooltip: personnelFieldTooltips.firstAidTraining,
  },
  {
    key: 'licensed',
    label: 'Patentato',
    type: 'checkbox',
    tooltip: personnelFieldTooltips.licensed,
  },
  {
    key: 'licenseLastUpdate',
    label: 'Ultimo aggiornamento patente',
    type: 'date',
    placeholder: 'Es: 15/09/2024',
    tooltip: personnelFieldTooltips.licenseLastUpdate,
    description: 'Compila solo se patentato.',
    disabled: (values) => !values.licensed,
    dependsOn: ['licensed'],
  },
  {
    key: 'licenseExpiry',
    label: 'Scadenza patente',
    type: 'date',
    placeholder: 'Es: 15/09/2026',
    tooltip: personnelFieldTooltips.licenseExpiry,
    description: 'Compila solo se patentato.',
    disabled: (values) => !values.licensed,
    dependsOn: ['licensed'],
  },
] as const satisfies readonly FieldConfig[];

const _personnelFormFieldKeyMatch: AssertFieldKeysMatch<
  typeof personnelFormFieldKeys,
  typeof personnelFormFields
> = personnelFormFields;
void _personnelFormFieldKeyMatch;

export type PersonnelFormValues = z.output<typeof personnelFormSchema>;
